import { Children, cloneElement, isValidElement, useEffect, type ReactElement, type ReactNode } from 'react'
import clsx from 'clsx'
import { useDashboardCustomization } from './DashboardCustomization'

interface DashboardGridProps {
  /** Rows in the grid. Defaults to 4 — the "4x4" a full tab fills. A tab with a
   *  fixed band above it (a KPI strip) can drop to 3 so its cells stay tall. */
  rows?: number
  /** Let the caller own responsive column/row utilities instead of applying
   * the fixed desktop 4-column/row template inline. Panel registration,
   * ordering, and visibility still use the same governed preference path. */
  responsive?: boolean
  className?: string
  children: ReactNode
}

/**
 * The tab body: a 4-column grid that fills the remaining height with no page
 * scroll. Cells are equal fractions (`1fr`), so a tab is tiled completely by its
 * panels' spans and every panel gets real height — the point of the regrid.
 * A panel that needs to scroll does so inside itself (Panel `scroll`), never by
 * growing the page.
 */
export function DashboardGrid({ rows = 4, responsive = false, className, children }: DashboardGridProps) {
  const customization = useDashboardCustomization()
  const entries = Children.toArray(children)
  const panels = entries.flatMap((child) => {
    if (!isValidElement<GridCellProps>(child) || !child.props.panelId) return []
    return [{ id: child.props.panelId, label: child.props.panelLabel ?? child.props.panelId }]
  })
  const panelSignature = panels.map((panel) => `${panel.id}:${panel.label}`).join('|')

  useEffect(() => {
    if (customization) customization.registerPanels(panels)
    // The signature is the stable primitive representation of JSX children.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customization?.registerPanels, panelSignature])

  const order = new Map((customization?.layout.order ?? []).map((id, index) => [id, index]))
  const hidden = new Set(customization?.layout.hidden ?? [])
  const arranged = customization
    ? entries
      .filter((child) => !(
        isValidElement<GridCellProps>(child)
        && child.props.panelId
        && hidden.has(child.props.panelId)
      ))
      .sort((left, right) => {
        const leftId = isValidElement<GridCellProps>(left) ? left.props.panelId : undefined
        const rightId = isValidElement<GridCellProps>(right) ? right.props.panelId : undefined
        return (order.get(leftId ?? '') ?? Number.MAX_SAFE_INTEGER)
          - (order.get(rightId ?? '') ?? Number.MAX_SAFE_INTEGER)
      })
      .map((child) => (
        isValidElement<GridCellProps>(child)
          ? cloneElement(child as ReactElement<GridCellProps>, {
            customized: customization.isCustomized,
          })
          : child
      ))
    : entries

  return (
    <div
      className={clsx(
        'grid min-h-0 min-w-0 flex-1 gap-2',
        !responsive && 'grid-cols-4',
        className,
      )}
      style={responsive ? undefined : { gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
    >
      {arranged}
    </div>
  )
}

export interface GridCellProps {
  /** Stable per-view key used only for the caller's layout preference. */
  panelId?: string
  /** Human-readable label shown by the Customize dialog. */
  panelLabel?: string
  /** Columns to span (1–4). */
  col?: number
  /** Rows to span. */
  row?: number
  /** Explicit 1-based column start. Omit to let the grid auto-place the cell —
   *  fine when every cell tiles evenly, needed when a tall cell sits beside a
   *  stack of shorter ones and auto-flow would leave holes. */
  colStart?: number
  /** Explicit 1-based row start. */
  rowStart?: number
  className?: string
  children: ReactNode
  /** Spans and starts are supplied by responsive utility classes. */
  responsive?: boolean
  /** Injected by DashboardGrid when a non-default layout must auto-pack. */
  customized?: boolean
}

/**
 * One tile of the grid. A flex column so the Panel (or bespoke block) inside
 * stretches to fill the cell; give that child `className="flex-1"` so its body
 * — a table, a chart — takes the height rather than sitting at natural size.
 *
 * Spans are inline styles, not Tailwind `col-span-*` classes, so an arbitrary
 * span survives the CSS purge and needs no safelist.
 */
export function GridCell({
  panelId,
  col = 1,
  row = 1,
  colStart,
  rowStart,
  className,
  children,
  responsive = false,
  customized = false,
}: GridCellProps) {
  return (
    <div
      data-dashboard-panel={panelId}
      className={clsx('flex min-h-0 min-w-0 flex-col', className)}
      style={responsive ? undefined : {
        gridColumn: !customized && colStart ? `${colStart} / span ${col}` : `span ${col} / span ${col}`,
        gridRow: !customized && rowStart ? `${rowStart} / span ${row}` : `span ${row} / span ${row}`,
      }}
    >
      {children}
    </div>
  )
}
